import React, { useState } from 'react';
import { Card, Grid, Container, Alert, Snackbar, FormHelperText, FormControlLabel, Switch } from '@mui/material';

// Material Dashboard 컴포넌트들
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// API 함수
import { apiRegisterUser } from "api/auth.api";

const MemberAdd = () => {
  // 폼 상태 관리
  const [formData, setFormData] = useState({
    userid: '',
    email: '',
    password: '',
    confirmpassword: '',
    phone: '',
    name: '',
    // true이면 기프티콘 결제(구매) 차단
    isGiftPurchaseLocked: false,
  });

  // UI 상태 관리
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // 입력 필드 변경 핸들러
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 유효성 검사 에러 초기화
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // 폼 유효성 검사
  const validateForm = () => {
    const errors = {};

    if (!formData.userid.trim()) {
      errors.userid = '사용자 ID를 입력해주세요';
    } else if (formData.userid.length < 3) {
      errors.userid = '사용자 ID는 3자 이상이어야 합니다';
    }

    if (!formData.email.trim()) {
      errors.email = '이메일을 입력해주세요';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = '올바른 이메일 형식을 입력해주세요';
    }

    if (!formData.password) {
      errors.password = '비밀번호를 입력해주세요';
    } else if (formData.password.length < 6) {
      errors.password = '비밀번호는 6자 이상이어야 합니다';
    }

    if (formData.password !== formData.confirmpassword) {
      errors.confirmpassword = '비밀번호가 일치하지 않습니다';
    }

    if (!formData.name.trim()) {
      errors.name = '이름을 입력해주세요';
    }

    if (!formData.phone.trim()) {
      errors.phone = '전화번호를 입력해주세요';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 회원가입 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // API 요청 데이터 구성 (백엔드 요구사항에 맞춤)
      const requestData = {
        userid: formData.userid,
        email: formData.email,
        password: formData.password,
        confirmpassword: formData.confirmpassword,
        phone: formData.phone,
        name: formData.name,
        // 모바일은 이 값을 보내지 않지만, Admin 회원추가에서는 선택적으로 전달
        isGiftPurchaseLocked: formData.isGiftPurchaseLocked,
      };

      console.log('회원가입 요청 데이터:', requestData);

      const response = await apiRegisterUser(requestData);
      
      if (response.data.accessToken) {
        setSuccess('회원가입이 성공적으로 완료되었습니다!');
        // 폼 초기화
        setFormData({
          userid: '',
          email: '',
          password: '',
          confirmpassword: '',
          phone: '',
          name: '',
          isGiftPurchaseLocked: false,
        });
      } else {
        setError(response.data.message || '회원가입 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('회원가입 오류:', error);
      setError(
        error.response?.data?.message || 
        '회원가입 중 오류가 발생했습니다. 다시 시도해주세요.'
      );
    } finally {
      setLoading(false);
    }
  };

  // 성공/에러 메시지 닫기
  const handleCloseMessage = () => {
    setSuccess('');
    setError('');
  };

  return (
    <Container maxWidth="lg">
      <Grid container justifyContent="center" sx={{ mt: 4, mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <MDBox sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
              <MDTypography variant="h4" sx={{ mb: 3, textAlign: 'center' }}>
                새 회원 등록
              </MDTypography>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* 사용자 ID */}
                  <Grid item xs={12} md={6}>
                    <MDBox sx={{ mb: 2 }}>
                      <MDTypography variant="button" fontWeight="regular" color="text">
                        사용자 ID *
                      </MDTypography>
                      <MDInput
                        type="text"
                        fullWidth
                        value={formData.userid}
                        onChange={(e) => handleInputChange('userid', e.target.value)}
                        error={!!validationErrors.userid}
                        placeholder="사용자 ID를 입력하세요"
                      />
                      {validationErrors.userid && (
                        <FormHelperText error sx={{ mt: 0.5 }}>
                          {validationErrors.userid}
                        </FormHelperText>
                      )}
                    </MDBox>
                  </Grid>

                  {/* 이메일 */}
                  <Grid item xs={12} md={6}>
                    <MDBox sx={{ mb: 2 }}>
                      <MDTypography variant="button" fontWeight="regular" color="text">
                        이메일 *
                      </MDTypography>
                      <MDInput
                        type="email"
                        fullWidth
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        error={!!validationErrors.email}
                        placeholder="이메일을 입력하세요"
                      />
                      {validationErrors.email && (
                        <FormHelperText error sx={{ mt: 0.5 }}>
                          {validationErrors.email}
                        </FormHelperText>
                      )}
                    </MDBox>
                  </Grid>

                  {/* 비밀번호 */}
                  <Grid item xs={12} md={6}>
                    <MDBox sx={{ mb: 2 }}>
                      <MDTypography variant="button" fontWeight="regular" color="text">
                        비밀번호 *
                      </MDTypography>
                      <MDInput
                        type="password"
                        fullWidth
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        error={!!validationErrors.password}
                        placeholder="비밀번호를 입력하세요"
                      />
                      {validationErrors.password && (
                        <FormHelperText error sx={{ mt: 0.5 }}>
                          {validationErrors.password}
                        </FormHelperText>
                      )}
                    </MDBox>
                  </Grid>

                  {/* 비밀번호 확인 */}
                  <Grid item xs={12} md={6}>
                    <MDBox sx={{ mb: 2 }}>
                      <MDTypography variant="button" fontWeight="regular" color="text">
                        비밀번호 확인 *
                      </MDTypography>
                      <MDInput
                        type="password"
                        fullWidth
                        value={formData.confirmpassword}
                        onChange={(e) => handleInputChange('confirmpassword', e.target.value)}
                        error={!!validationErrors.confirmpassword}
                        placeholder="비밀번호를 다시 입력하세요"
                      />
                      {validationErrors.confirmpassword && (
                        <FormHelperText error sx={{ mt: 0.5 }}>
                          {validationErrors.confirmpassword}
                        </FormHelperText>
                      )}
                    </MDBox>
                  </Grid>

                  {/* 이름 */}
                  <Grid item xs={12} md={6}>
                    <MDBox sx={{ mb: 2 }}>
                      <MDTypography variant="button" fontWeight="regular" color="text">
                        이름 *
                      </MDTypography>
                      <MDInput
                        type="text"
                        fullWidth
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        error={!!validationErrors.name}
                        placeholder="이름을 입력하세요"
                      />
                      {validationErrors.name && (
                        <FormHelperText error sx={{ mt: 0.5 }}>
                          {validationErrors.name}
                        </FormHelperText>
                      )}
                    </MDBox>
                  </Grid>

                  {/* 전화번호 */}
                  <Grid item xs={12} md={6}>
                    <MDBox sx={{ mb: 2 }}>
                      <MDTypography variant="button" fontWeight="regular" color="text">
                        전화번호 *
                      </MDTypography>
                      <MDInput
                        type="tel"
                        fullWidth
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        error={!!validationErrors.phone}
                        placeholder="전화번호를 입력하세요"
                      />
                      {validationErrors.phone && (
                        <FormHelperText error sx={{ mt: 0.5 }}>
                          {validationErrors.phone}
                        </FormHelperText>
                      )}
                    </MDBox>
                  </Grid>

                  {/* 기프티콘 결제 차단 */}
                  <Grid item xs={12}>
                    <MDBox sx={{ mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isGiftPurchaseLocked}
                            onChange={(e) => handleInputChange('isGiftPurchaseLocked', e.target.checked)}
                            color="info"
                          />
                        }
                        label="기프티콘 결제(구매) 차단"
                      />
                      <MDTypography variant="caption" color="text">
                        체크 시 해당 회원은 기프티콘 구매가 불가합니다. (미체크/미전송 시 구매 가능)
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>

                {/* 제출 버튼 */}
                <MDBox sx={{ mt: 3, width: '100%' }}>
                  <MDButton
                    type="submit"
                    variant="gradient"
                    color="info"
                    fullWidth
                    disabled={loading}
                  >
                    {loading ? '처리 중...' : '회원 등록'}
                  </MDButton>
                </MDBox>
              </form>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      {/* 성공 메시지 */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseMessage} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* 에러 메시지 */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseMessage} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MemberAdd;